import { useState, useEffect, useCallback, useId } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AnimatePresence, motion } from "framer-motion";
import type {
  CustomFieldsSchema,
  OpportunityStage,
  OpportunityStageCategory,
  OpportunityStageRequiredFields,
} from "@forgepoint/shared";
import {
  DEFAULT_OPPORTUNITY_NAMING_GUIDANCE,
  DEFAULT_OPPORTUNITY_PROBABILITY,
  EMPTY_STAGE_REQUIRED_FIELDS,
  OpportunityStatusEnum,
  areStageRequiredFieldsEqual,
  getCustomFieldNames,
} from "@forgepoint/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOpportunityStagesStore } from "@/modules/opportunities/opportunityStagesStore";
import { useOpportunitySettingsStore } from "@/store/opportunitySettingsStore";
import { useERPIntegrationsStore } from "@/store/erpIntegrationsStore";
import { SectionLabel } from "./SectionLabel";
import { SortableStageRow } from "./SortableStageRow";
import { CustomFieldsSchemaEditor } from "./CustomFieldsSchemaEditor";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { UnsavedChangesBar } from "@/modules/standardAgent/components/UnsavedChangesBar";

/** Describes a custom field affected by schema changes with optional removed option details. */
interface AffectedField {
  fieldName: string;
  removedOptions?: string[];
}

interface LocalStageRow {
  /** Stable key for DnD — real ID for existing stages, temp ID for new ones. */
  key: string;
  /** Null for new stages that haven't been created yet. */
  stageId: string | null;
  name: string;
  description: string;
  probability: number | null;
  /** The analytics category this stage maps to. */
  category: OpportunityStageCategory;
  /** True for seeded default stages — cannot be archived. */
  isDefault: boolean;
  sortOrder: number;
  requiredFields: OpportunityStageRequiredFields;
}

/**
 * Builds local rows from the store's active stages.
 * @param stages - Map of stage ID to stage object
 * @returns Array of local stage row objects sorted by sortOrder
 */
function buildRowsFromStore(
  stages: Map<string, OpportunityStage>,
): LocalStageRow[] {
  return Array.from(stages.values())
    .filter((s) => !s.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => ({
      key: s.id,
      stageId: s.id,
      name: s.name,
      description: s.description ?? "",
      probability: s.probability,
      category: s.category,
      isDefault: s.isDefault,
      sortOrder: s.sortOrder,
      requiredFields: s.requiredFields,
    }));
}

interface OpportunitySettingsPanelProps {
  isAdmin: boolean;
}

/**
 * Settings panel for managing opportunity stages.
 * Supports adding, renaming, reordering, and archiving custom stages.
 * @param props - Contains isAdmin flag
 */
export function OpportunitySettingsPanel({
  isAdmin,
}: OpportunitySettingsPanelProps) {
  const dndId = useId();
  const { stages, fetchStages, createStage, updateStage, hasFetched } =
    useOpportunityStagesStore();
  const opportunitySettings = useOpportunitySettingsStore((s) => s.settings);
  const fetchOpportunitySettings = useOpportunitySettingsStore(
    (s) => s.fetchSettings,
  );
  const updateOpportunitySettings = useOpportunitySettingsStore(
    (s) => s.updateSettings,
  );
  const erpSyncs = useERPIntegrationsStore((s) => s.syncs);
  const fetchSyncs = useERPIntegrationsStore((s) => s.fetchSyncs);

  const [rows, setRows] = useState<LocalStageRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [nextTempId, setNextTempId] = useState(1);
  const [defaultProbabilityInput, setDefaultProbabilityInput] = useState(
    String(DEFAULT_OPPORTUNITY_PROBABILITY),
  );
  const [autoCloseAfterDays, setAutoCloseAfterDays] = useState(0);
  const [autoCloseTargetStageId, setAutoCloseTargetStageId] = useState("");
  const [opportunityNamingGuidanceInput, setOpportunityNamingGuidanceInput] =
    useState(DEFAULT_OPPORTUNITY_NAMING_GUIDANCE);
  const [customFieldsSchema, setCustomFieldsSchema] =
    useState<CustomFieldsSchema | null>(null);
  const [originalCustomFieldsSchema, setOriginalCustomFieldsSchema] =
    useState<CustomFieldsSchema | null>(null);
  const [showFieldMappingWarning, setShowFieldMappingWarning] = useState(false);
  const [affectedMappedFields, setAffectedMappedFields] = useState<
    AffectedField[]
  >([]);

  // When no auto-close target is explicitly chosen, the form pre-fills with the
  // org's default Lost stage so the dropdown shows a concrete selection that
  // matches what is saved.
  const activeStages = Array.from(stages.values())
    .filter((s) => !s.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const lostStages = activeStages.filter(
    (s) => s.category === OpportunityStatusEnum.LOST,
  );
  const defaultLostStageId =
    lostStages.find((s) => s.isDefault)?.id ?? lostStages[0]?.id ?? "";
  const resolvedTargetStageId = autoCloseTargetStageId || defaultLostStageId;

  useEffect(() => {
    if (!hasFetched) {
      fetchStages();
    }
  }, [hasFetched, fetchStages]);

  useEffect(() => {
    fetchOpportunitySettings();
    fetchSyncs();
  }, [fetchOpportunitySettings, fetchSyncs]);

  // Sync settings inputs from store
  useEffect(() => {
    if (!isSaving) {
      setDefaultProbabilityInput(
        String(
          opportunitySettings?.defaultProbability ??
            DEFAULT_OPPORTUNITY_PROBABILITY,
        ),
      );
      setOpportunityNamingGuidanceInput(
        opportunitySettings?.opportunityNamingGuidance ??
          DEFAULT_OPPORTUNITY_NAMING_GUIDANCE,
      );
      setCustomFieldsSchema(opportunitySettings?.customFieldsSchema ?? null);
      setOriginalCustomFieldsSchema(
        opportunitySettings?.customFieldsSchema ?? null,
      );
      setAutoCloseAfterDays(opportunitySettings?.autoCloseAfterDays ?? 0);
      setAutoCloseTargetStageId(
        opportunitySettings?.autoCloseTargetStageId ?? "",
      );
    }
  }, [opportunitySettings, isSaving]);

  // Sync local state from store when stages change (and not during save)
  useEffect(() => {
    if (!isSaving) {
      setRows(buildRowsFromStore(stages));
    }
  }, [stages, isSaving]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const isDirty = (() => {
    const storedDefaultProbability =
      opportunitySettings?.defaultProbability ??
      DEFAULT_OPPORTUNITY_PROBABILITY;
    const storedNamingGuidance =
      opportunitySettings?.opportunityNamingGuidance ??
      DEFAULT_OPPORTUNITY_NAMING_GUIDANCE;
    if (
      defaultProbabilityInput !== String(storedDefaultProbability) ||
      opportunityNamingGuidanceInput !== storedNamingGuidance
    ) {
      return true;
    }
    if (
      JSON.stringify(customFieldsSchema) !==
      JSON.stringify(originalCustomFieldsSchema)
    ) {
      return true;
    }
    if (
      opportunitySettings &&
      autoCloseAfterDays !== (opportunitySettings.autoCloseAfterDays ?? 0)
    ) {
      return true;
    }
    if (
      opportunitySettings &&
      autoCloseTargetStageId !==
        (opportunitySettings.autoCloseTargetStageId ?? "")
    ) {
      return true;
    }
    const storeRows = buildRowsFromStore(stages);
    const nonEmptyRows = rows.filter(
      (r) =>
        r.stageId ||
        r.name.trim() ||
        r.description.trim() ||
        r.probability !== null ||
        r.requiredFields.standard.length > 0 ||
        r.requiredFields.custom.length > 0,
    );
    if (nonEmptyRows.length !== storeRows.length) {
      return true;
    }
    return nonEmptyRows.some((row, i) => {
      const storeRow = storeRows[i];
      return (
        row.name !== storeRow.name ||
        row.description !== storeRow.description ||
        row.category !== storeRow.category ||
        row.probability !== storeRow.probability ||
        row.sortOrder !== i ||
        !areStageRequiredFieldsEqual(
          row.requiredFields,
          storeRow.requiredFields,
        )
      );
    });
  })();

  const nonEmptyNames = rows.filter((r) => r.name.trim().length > 0);
  const lowerNames = nonEmptyNames.map((r) => r.name.trim().toLowerCase());
  const duplicateNameSet = new Set(
    lowerNames.filter((v, i) => lowerNames.indexOf(v) !== i),
  );
  const hasDuplicates = duplicateNameSet.size > 0;
  const hasEmptyNames = rows.some((r) => r.name.trim().length === 0);
  const customFieldNames = getCustomFieldNames(customFieldsSchema);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!isAdmin) {
        return;
      }
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const rowKeys = rows.map((r) => r.key);
      const oldIndex = rowKeys.indexOf(String(active.id));
      const newIndex = rowKeys.indexOf(String(over.id));
      const reordered = arrayMove(rows, oldIndex, newIndex).map((r, i) => ({
        ...r,
        sortOrder: i,
      }));
      setRows(reordered);
    },
    [isAdmin, rows],
  );

  const handleAddStage = () => {
    const tempKey = `new-${nextTempId}`;
    setNextTempId((n) => n + 1);
    setRows((prev) => [
      ...prev,
      {
        key: tempKey,
        stageId: null,
        name: "",
        description: "",
        probability: null,
        category: OpportunityStatusEnum.QUOTE_IN_PROGRESS,
        isDefault: false,
        sortOrder: prev.length,
        requiredFields: EMPTY_STAGE_REQUIRED_FIELDS,
      },
    ]);
  };

  const handleDiscard = () => {
    setRows(buildRowsFromStore(stages));
    setDefaultProbabilityInput(
      String(
        opportunitySettings?.defaultProbability ??
          DEFAULT_OPPORTUNITY_PROBABILITY,
      ),
    );
    setAutoCloseAfterDays(opportunitySettings?.autoCloseAfterDays ?? 0);
    setAutoCloseTargetStageId(
      opportunitySettings?.autoCloseTargetStageId ?? "",
    );
    setOpportunityNamingGuidanceInput(
      opportunitySettings?.opportunityNamingGuidance ??
        DEFAULT_OPPORTUNITY_NAMING_GUIDANCE,
    );
    setCustomFieldsSchema(opportunitySettings?.customFieldsSchema ?? null);
  };

  /**
   * Returns custom fields that were removed, had their type changed, or had
   * mapped select options removed, and are currently mapped in at least one ERP
   * sync's opportunityCustomFieldMapping. Includes specific removed options per field.
   * @param oldSchema - The original schema before edits
   * @param newSchema - The schema being saved
   * @returns Array of affected fields with optional removed option details
   */
  const getMappedAffectedFields = (
    oldSchema: CustomFieldsSchema | null,
    newSchema: CustomFieldsSchema | null,
  ): AffectedField[] => {
    if (!oldSchema) {
      return [];
    }

    const fieldLevelAffected = Object.keys(oldSchema.properties).filter(
      (name) => {
        const newProp = newSchema?.properties[name];
        if (!newProp) {
          return true;
        }
        return oldSchema.properties[name].type[0] !== newProp.type[0];
      },
    );

    const resultMap = new Map<string, AffectedField>();
    for (const sync of erpSyncs) {
      const mapping = sync.integrationConfig?.opportunityCustomFieldMapping;
      if (!isNonNullRecord(mapping)) {
        continue;
      }

      for (const key of Object.keys(mapping)) {
        if (fieldLevelAffected.includes(key) && !resultMap.has(key)) {
          resultMap.set(key, { fieldName: key });
        }
      }

      if (!newSchema) {
        continue;
      }
      for (const [fieldName, fieldMapping] of Object.entries(mapping)) {
        if (resultMap.has(fieldName)) {
          continue;
        }
        if (!isSelectFieldMappingValue(fieldMapping)) {
          continue;
        }
        const oldProp = oldSchema.properties[fieldName];
        const newProp = newSchema.properties[fieldName];
        if (!oldProp?.enum || !newProp) {
          continue;
        }
        const newOptions = new Set(
          (newProp.enum ?? []).filter(
            (v): v is string => typeof v === "string",
          ),
        );
        const removedOptions = Object.keys(fieldMapping.optionMapping).filter(
          (opt) => !newOptions.has(opt),
        );
        if (removedOptions.length > 0) {
          resultMap.set(fieldName, { fieldName, removedOptions });
        }
      }
    }
    return [...resultMap.values()];
  };

  const handleSave = async () => {
    if (hasEmptyNames) {
      toast.error("All stages must have a name");
      return;
    }
    if (hasDuplicates) {
      toast.error("Stage names must be unique");
      return;
    }
    const parsedProbability = Number(defaultProbabilityInput);
    if (
      !Number.isInteger(parsedProbability) ||
      parsedProbability < 0 ||
      parsedProbability > 100
    ) {
      toast.error(
        "Default probability must be a whole number between 0 and 100",
      );
      return;
    }

    const mapped = getMappedAffectedFields(
      originalCustomFieldsSchema,
      customFieldsSchema,
    );
    if (mapped.length > 0) {
      setAffectedMappedFields(mapped);
      setShowFieldMappingWarning(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    setIsSaving(true);
    try {
      const parsedProbability = Number(defaultProbabilityInput);
      const desiredAutoCloseAfterDays =
        autoCloseAfterDays > 0 ? autoCloseAfterDays : null;
      const desiredTargetStageId =
        autoCloseAfterDays > 0 ? resolvedTargetStageId || null : null;
      const storedDefaultProbability =
        opportunitySettings?.defaultProbability ??
        DEFAULT_OPPORTUNITY_PROBABILITY;
      const normalizedNamingGuidance = normalizeNamingGuidanceForSave(
        opportunityNamingGuidanceInput,
      );
      const storedNamingGuidance =
        opportunitySettings?.opportunityNamingGuidance ?? null;
      const schemaChanged =
        JSON.stringify(customFieldsSchema) !==
        JSON.stringify(originalCustomFieldsSchema);
      const shouldSaveSettings =
        parsedProbability !== storedDefaultProbability ||
        desiredAutoCloseAfterDays !==
          (opportunitySettings?.autoCloseAfterDays ?? null) ||
        desiredTargetStageId !==
          (opportunitySettings?.autoCloseTargetStageId ?? null) ||
        normalizedNamingGuidance !== storedNamingGuidance ||
        schemaChanged;

      if (shouldSaveSettings) {
        const success = await updateOpportunitySettings({
          defaultProbability: parsedProbability,
          autoCloseAfterDays: desiredAutoCloseAfterDays,
          autoCloseTargetStageId: desiredTargetStageId,
          opportunityNamingGuidance: normalizedNamingGuidance,
          ...(schemaChanged ? { customFieldsSchema } : {}),
        });
        if (!success) {
          toast.error("Failed to save opportunity settings");
          return;
        }
      }

      for (const [index, row] of rows.entries()) {
        if (!row.stageId) {
          const { error } = await createStage({
            name: row.name.trim(),
            description: row.description.trim() || null,
            category: row.category,
            sortOrder: index,
            probability: row.probability,
            requiredFields: row.requiredFields,
          });
          if (error) {
            toast.error(`Failed to create stage "${row.name}": ${error}`);
            return;
          }
        } else {
          const storeStage = stages.get(row.stageId);
          const categoryChanged = storeStage
            ? storeStage.category !== row.category
            : false;
          if (
            storeStage &&
            (storeStage.name !== row.name.trim() ||
              (storeStage.description ?? "") !== row.description.trim() ||
              categoryChanged ||
              storeStage.sortOrder !== index ||
              storeStage.probability !== row.probability ||
              !areStageRequiredFieldsEqual(
                storeStage.requiredFields,
                row.requiredFields,
              ))
          ) {
            const { error } = await updateStage(row.stageId, {
              name: row.name.trim(),
              description: row.description.trim() || null,
              category: row.category,
              sortOrder: index,
              probability: row.probability,
              requiredFields: row.requiredFields,
            });
            if (error) {
              toast.error(`Failed to update stage "${row.name}": ${error}`);
              return;
            }
          }
        }
      }
      await fetchStages();
      toast.success("Opportunity settings saved");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveRow = async (index: number) => {
    const row = rows[index];
    if (row.isDefault) {
      toast.error("Cannot archive a default stage");
      return;
    }
    if (!row.stageId) {
      setRows((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setIsSaving(true);
    try {
      const { error } = await updateStage(row.stageId, {
        archivedAt: new Date(),
      });
      if (error) {
        toast.error(`Failed to archive stage: ${error}`);
        return;
      }
      await fetchStages();
      toast.success(`Stage "${row.name}" archived`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRowUpdate = (
    index: number,
    update: Partial<
      Pick<
        LocalStageRow,
        "name" | "description" | "probability" | "category" | "requiredFields"
      >
    >,
  ) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...update } : r)),
    );
  };

  return (
    <>
      <div className="space-y-4">
        <SectionLabel title="Opportunities" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="opportunity-settings-default-win-probability"
              label="Default Win Probability"
              description="The default probability (%) applied when a new opportunity is created."
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  className="w-[100px]"
                  value={defaultProbabilityInput}
                  onChange={(e) => setDefaultProbabilityInput(e.target.value)}
                  disabled={!isAdmin || isSaving}
                  min={0}
                  max={100}
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </SettingsRow>
            <SettingsRow
              label="Move stale opportunities"
              description="Automatically move an opportunity to another stage when it hasn't been updated for this many days. Set to 0 to disable."
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  className="w-[100px]"
                  value={autoCloseAfterDays}
                  onChange={(e) => {
                    const next = Number.parseInt(e.target.value, 10);
                    setAutoCloseAfterDays(
                      Number.isNaN(next) ? 0 : Math.max(0, next),
                    );
                  }}
                  disabled={!isAdmin || isSaving}
                  min={0}
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </SettingsRow>

            <AnimatePresence initial={false}>
              {autoCloseAfterDays > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <SettingsRow
                    label="Move stale opportunities to"
                    description="The stage a stale opportunity lands in once it's moved."
                  >
                    <Select
                      value={resolvedTargetStageId}
                      onValueChange={setAutoCloseTargetStageId}
                      disabled={!isAdmin || isSaving}
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="Select a stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeStages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SettingsRow>
                </motion.div>
              )}
            </AnimatePresence>

            <SettingsRow
              id="opportunity-settings-naming-guidance"
              label="Opportunity Naming Guidance"
              description="Instructions used when the agent names opportunities."
            >
              <Textarea
                className="w-full min-w-0 max-w-[520px]"
                minRows={5}
                maxRows={10}
                value={opportunityNamingGuidanceInput}
                onChange={(e) =>
                  setOpportunityNamingGuidanceInput(e.target.value)
                }
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>
          </div>
        </Card>

        <SectionLabel
          id="opportunity-settings-stages"
          title="Opportunity Stages"
        />
        <Card className="shadow-sm overflow-hidden">
          <div className="@container/stages px-4 py-3.5">
            <p className="text-xs text-muted-foreground mb-3">
              Define the stages an opportunity moves through. Each stage maps to
              a category that's used for analytics.
            </p>

            <div className="flex items-center gap-2 px-1 pb-1 text-xs font-medium text-muted-foreground">
              <span className="w-4 shrink-0" />
              <span className="flex-1">Name</span>
              <span className="hidden w-[180px] @[616px]/stages:block">
                Category
              </span>
              <span className="hidden w-[120px] @[616px]/stages:block">
                Prob.
              </span>
              <span className="hidden w-[180px] @[860px]/stages:block">
                Required fields
              </span>
              <span className="w-7" />
            </div>

            <DndContext
              id={dndId}
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rows.map((r) => r.key)}
                strategy={verticalListSortingStrategy}
              >
                {rows.map((row, index) => (
                  <SortableStageRow
                    key={row.key}
                    id={row.key}
                    name={row.name}
                    description={row.description}
                    probability={row.probability}
                    category={row.category}
                    isDefault={row.isDefault}
                    categoryEditable={!row.isDefault}
                    isDuplicate={
                      row.name.trim().length > 0 &&
                      duplicateNameSet.has(row.name.trim().toLowerCase())
                    }
                    disabled={!isAdmin || isSaving}
                    requiredFields={row.requiredFields}
                    customFieldNames={customFieldNames}
                    otherStages={rows
                      .filter(
                        (other) =>
                          other.key !== row.key && other.name.trim().length > 0,
                      )
                      .map((other) => ({
                        name: other.name.trim(),
                        requiredFields: other.requiredFields,
                      }))}
                    onNameChange={(name) => handleRowUpdate(index, { name })}
                    onDescriptionChange={(description) =>
                      handleRowUpdate(index, { description })
                    }
                    onProbabilityChange={(probability) =>
                      handleRowUpdate(index, { probability })
                    }
                    onCategoryChange={(category) =>
                      handleRowUpdate(index, { category })
                    }
                    onRequiredFieldsChange={(requiredFields) =>
                      handleRowUpdate(index, { requiredFields })
                    }
                    onArchive={() => handleArchiveRow(index)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {hasDuplicates && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs text-destructive mt-1"
              >
                Duplicate stage names are not allowed
              </motion.p>
            )}

            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddStage}
                disabled={isSaving}
                className="mt-2 h-8 text-xs px-3 text-muted-foreground hover:text-foreground"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add Stage
              </Button>
            )}
          </div>
        </Card>

        <SectionLabel title="Custom Fields" />
        <Card className="shadow-sm overflow-hidden">
          <div className="px-4 py-3.5">
            <p className="text-sm font-medium mb-1">
              Opportunity custom fields
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Define custom fields that appear on all opportunities.
            </p>
            <CustomFieldsSchemaEditor
              schema={customFieldsSchema}
              onChange={setCustomFieldsSchema}
              disabled={!isAdmin || isSaving}
            />
          </div>
        </Card>

        <UnsavedChangesBar
          isDirty={isDirty}
          onSave={handleSave}
          onDiscard={handleDiscard}
          isSaving={isSaving}
        />
      </div>

      {/* ── Custom Field Mapping Warning Dialog ── */}
      <AlertDialog
        open={showFieldMappingWarning}
        onOpenChange={setShowFieldMappingWarning}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ERP sync mappings will be removed
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                <p>
                  The following custom field mappings will be affected. You will
                  need to re-configure them in integration settings if you
                  proceed.
                </p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {affectedMappedFields.map((field) => (
                    <li key={field.fieldName}>
                      <span className="font-medium text-foreground">
                        {field.fieldName}
                      </span>
                      {field.removedOptions ? (
                        <span>
                          {" "}
                          — mapped options removed:{" "}
                          {field.removedOptions.join(", ")}
                        </span>
                      ) : (
                        <span> — field removed or type changed</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowFieldMappingWarning(false);
                performSave();
              }}
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function normalizeNamingGuidanceForSave(guidance: string): string | null {
  const trimmed = guidance.trim();
  if (!trimmed || trimmed === DEFAULT_OPPORTUNITY_NAMING_GUIDANCE.trim()) {
    return null;
  }
  return trimmed;
}

/**
 * Type guard that checks whether a value is a non-null object usable as a record.
 * @param value - The value to check
 * @returns True if the value is a non-null object
 */
function isNonNullRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Type guard that checks whether a mapping value is a SelectFieldMapping object
 * with erpProperty and optionMapping.
 * @param value - The mapping value to check
 * @returns True if the value matches the SelectFieldMapping shape
 */
function isSelectFieldMappingValue(
  value: unknown,
): value is { erpProperty: string; optionMapping: Record<string, string> } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("erpProperty" in value) || !("optionMapping" in value)) {
    return false;
  }
  return (
    typeof value.erpProperty === "string" &&
    typeof value.optionMapping === "object" &&
    value.optionMapping !== null
  );
}
